import React, { ReactNode, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';
import Map, { Marker, Source, Layer, MapRef } from 'react-map-gl/mapbox'
import { MonitorByAreaResponse } from 'domain/payload/response/monitor/MonitorByAreaResp'
import MonitorProgressChart from './MonitorProgressChart'
import LoadingOverlay from '../userDetailModal/components/LoadingOverlay'
import { HeatmapLayer } from 'mapbox-gl'
import { MAPBOX_TOKEN } from 'config/Mapbox'

interface AreaDetailModalProps {
  show: boolean
  onClose: () => void
  areaName: string
  areaDescription: string
  projectId: string
  data: MonitorByAreaResponse | null
  loading: boolean
}

const defaultCenter = { lat: -6.200000, lng: 106.816666 }

const heatmapLayerStyle: HeatmapLayer = {
  id: 'heatmap-layer',
  type: 'heatmap',
  source: 'heatmap-source',
  paint: {
    'heatmap-radius': 8,
    'heatmap-opacity': 0.65,
    'heatmap-intensity': 1,
    'heatmap-weight': 1,
  },
}

interface PlaceholderProps {
  height?: number;
  children?: ReactNode;
}

function Placeholder({ height = 180, children }: PlaceholderProps) {
  return (
    <div className="border rounded w-100 p-3 d-flex flex-column"
      style={{ height, background: '#ffffff' }}>
      {children}
    </div>
  )
}

const AreaDetailModal: React.FC<AreaDetailModalProps> = ({
  show,
  onClose,
  areaName,
  areaDescription,
  projectId,
  data,
  loading,
}) => {
  const [mapMode, setMapMode] = useState<'point' | 'heatmap'>('point')
  const [mapLoaded, setMapLoaded] = useState(false)
  const [mapIdle, setMapIdle] = useState(false)
  const [userCurrentPage, setUserCurrentPage] = useState(1)
  const usersPerPage = 10
  const mapRef = useRef<MapRef | null>(null)

  const { navRef: modeNavRef, indicatorStyle: modeIndicatorStyle } = useNavPillsIndicator(mapMode);

  const markers = useMemo(() => {
    if (!data?.results) return []
    return data.results
      .filter(r => r.latitude != null && r.longitude != null)
      .map(r => ({
        id: r.id,
        lat: Number(r.latitude),
        lng: Number(r.longitude),
        title: r.jobName,
      }))
  }, [data])

  const mapCenter = useMemo(() => {
    if (markers.length > 0) return { lat: markers[0].lat, lng: markers[0].lng }
    return defaultCenter
  }, [markers])

  const sortedResults = useMemo(() => {
    if (!data?.results) return []
    return [...data.results]
      .filter(r => r.latitude && r.longitude)
      .sort((a, b) => new Date(a.submitDate).getTime() - new Date(b.submitDate).getTime())
  }, [data])

  const heatmapGeoJSON = useMemo((): GeoJSON.FeatureCollection => ({
    type: 'FeatureCollection',
    features: sortedResults.map(r => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [Number(r.longitude), Number(r.latitude)],
      },
      properties: {},
    })),
  }), [sortedResults])

  const sortedUsers = useMemo(() => {
    if (!data?.contributors) return []
    return [...data.contributors].sort((a, b) => a.name.localeCompare(b.name))
  }, [data])

  const totalUserPages = Math.ceil(sortedUsers.length / usersPerPage)

  const paginatedUsers = useMemo(() => {
    const start = (userCurrentPage - 1) * usersPerPage
    return sortedUsers.slice(start, start + usersPerPage)
  }, [sortedUsers, userCurrentPage])

  const handleUserPageChange = (page: number) => {
    if (page < 1 || page > totalUserPages) return
    setUserCurrentPage(page)
  }

  const fitBoundsToMarkers = useCallback(() => {
    if (!mapRef.current || markers.length === 0) return

    // Hitung bounding box manual
    const lngs = markers.map(m => m.lng)
    const lats = markers.map(m => m.lat)
    const minLng = Math.min(...lngs)
    const maxLng = Math.max(...lngs)
    const minLat = Math.min(...lats)
    const maxLat = Math.max(...lats)

    mapRef.current.fitBounds(
      [[minLng, minLat], [maxLng, maxLat]],
      { padding: 50, duration: 500 }
    )
  }, [markers])

  useEffect(() => { setUserCurrentPage(1) }, [data])

  useEffect(() => {
    if (show && mapRef.current) {
      setTimeout(() => {
        mapRef.current?.resize()
        fitBoundsToMarkers()
      }, 200)
    }
  }, [show])

  useEffect(() => {
    if (!show) {
      setMapIdle(false)
      setMapLoaded(false)
    }
  }, [show])

  useEffect(() => {
    if (show && mapLoaded && markers.length > 0) {
      setTimeout(() => {
        fitBoundsToMarkers();
      }, 300);
    }
  }, [show, mapLoaded, markers]);

  if (!show) return null

  return (
    <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <LoadingOverlay loading={loading} />

          <div className="modal-header">
            <h5 className="modal-title">Area Detail</h5>
            <button className="btn-close" onClick={onClose} disabled={loading} />
          </div>

          <div className="px-4 pt-3">
            <div className="mb-2">
              <small className="text-muted">Name</small>
              <div className="fw-semibold">{areaName}</div>
            </div>
            <div className='mb-2'>
              <small className="text-muted">Location</small>
              <div>{areaDescription}</div>
            </div>
          </div>

          <div className="modal-body">
            {!loading && data && (
              <>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-4">
                    <Placeholder height={260}>
                      <div className="d-flex flex-column h-100">
                        <div>
                          <p className="mb-2" style={{ fontSize: 16, fontWeight: 500 }}>
                            Summary
                          </p>
                        </div>

                        <div className="row flex-grow-1">
                          <div className="col-6 d-flex">
                            <div className="card flex-fill mb-4 p-2 d-flex align-items-center justify-content-center flex-column">
                              <h6 className="mb-1">Total Progress</h6>
                              <p className="mb-0 fw-bold">
                                {data.summaryData.completeTotal} / {data.summaryData.jobTotal}
                              </p>
                            </div>
                          </div>

                          <div className="col-6 d-flex">
                            <div className="card flex-fill mb-4 p-2 d-flex align-items-center justify-content-center flex-column">
                              <h6 className="mb-1">Data Hari Ini</h6>
                              <p className="mb-0 fw-bold">
                                {data.summaryData.dataToday}
                              </p>
                            </div>
                          </div>

                          <div className="col-12 d-flex">
                            <div className="card flex-fill p-2 d-flex align-items-center justify-content-center flex-column">
                              <h6 className="mb-1">
                                {data.summaryData.results
                                  .slice()
                                  .sort((a, b) => a.sort - b.sort)
                                  .map(r => r.label)
                                  .join(' - ')
                                }
                              </h6>
                              <p className="mb-0 fw-bold">
                                {data.summaryData.results
                                  .slice()
                                  .sort((a, b) => a.sort - b.sort)
                                  .map(r => r.value)
                                  .join(' - ')
                                }
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Placeholder>
                  </div>

                  {data && (
                    <div className="col-12 col-md-8">
                      <Placeholder height={360}>
                        <MonitorProgressChart
                          source={data}
                          height={300}
                        />
                      </Placeholder>
                    </div>
                  )}
                </div>

                <div className="row">
                  <div className="col-12 col-md-6">
                    <Placeholder height={470}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="fw-semibold mb-0">Map View</h6>
                        <div ref={modeNavRef} className="nav-pills-animated h-100" style={{ height: 50, maxHeight: 50 }}>
                          <div
                            className="nav-pill-indicator"
                            style={modeIndicatorStyle}
                          />
                          <button
                            type='button'
                            className={`h-100 nav-link ${mapMode === "point" ? "active" : ""
                              }`}
                            onClick={() => setMapMode('point')}
                          >
                            Point
                          </button>
                          <button
                            type='button'
                            className={`h-100 nav-link ${mapMode === "heatmap" ? "active" : ""
                              }`}
                            onClick={() => setMapMode('heatmap')}
                          >
                            Heatmap
                          </button>
                        </div>
                      </div>

                      <div className="flex-grow-1 overflow-hidden" style={{ borderRadius: 8 }}>
                        <Map
                          ref={mapRef}
                          mapboxAccessToken={MAPBOX_TOKEN}
                          initialViewState={{
                            longitude: mapCenter.lng,
                            latitude: mapCenter.lat,
                            zoom: 13,
                          }}
                          style={{ width: '100%', height: '100%' }}
                          mapStyle="mapbox://styles/mapbox/streets-v12"
                          onLoad={() => {
                            setTimeout(fitBoundsToMarkers, 300)
                          }}
                        >
                          {mapMode === 'point' && sortedResults.map((r, index) => (
                            <Marker
                              key={`${r.id}-${index}`}
                              longitude={Number(r.longitude)}
                              latitude={Number(r.latitude)}
                            >
                              <img
                                src="/assets/markers/icon-marker.svg"
                                style={{ width: 12, height: 12 }}
                                alt=""
                              />
                            </Marker>
                          ))}

                          {mapMode === 'heatmap' && (
                            <Source id="heatmap-source" type="geojson" data={heatmapGeoJSON}>
                              <Layer {...heatmapLayerStyle} />
                            </Source>
                          )}
                        </Map>
                      </div>
                    </Placeholder>
                  </div>

                  <div className="col-12 col-md-6">
                    <Placeholder height={470}>
                      <h6 className="fw-semibold mb-2">User</h6>

                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Name</th>
                              {data.contributors?.length > 0 &&
                                data.contributors[0].results
                                  ?.slice()
                                  .sort((a, b) => a.sort - b.sort)
                                  .map(r => (
                                    <th key={r.sort}>{r.label}</th>
                                  ))
                              }
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedUsers.map(c => (
                              <tr key={c.id}>
                                <td>{c.name}</td>
                                {c.results
                                  .sort((a, b) => a.sort - b.sort)
                                  .map(r => (
                                    <td key={`cell-${c.id}-${r.sort}`}>{r.value}</td>
                                  ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="d-flex justify-content-end align-items-center mt-3">
                          <div className="d-flex align-items-center">
                            <span className="me-3">
                              {`${(userCurrentPage - 1) * usersPerPage + 1}–${Math.min(
                                userCurrentPage * usersPerPage,
                                sortedUsers.length
                              )} of ${sortedUsers.length}`}
                            </span>

                            <nav>
                              <ul className="pagination mb-0">
                                <li className={`page-item ${userCurrentPage === 1 ? 'disabled' : ''}`}>
                                  <button
                                    className="page-link"
                                    style={{ border: 0 }}
                                    onClick={() => handleUserPageChange(1)}
                                  >
                                    <i className="fa fa-angle-double-left" />
                                  </button>
                                </li>

                                <li className={`page-item ${userCurrentPage === 1 ? 'disabled' : ''}`}>
                                  <button
                                    className="page-link"
                                    style={{ border: 0 }}
                                    onClick={() => handleUserPageChange(userCurrentPage - 1)}
                                  >
                                    <i className="fa fa-angle-left" />
                                  </button>
                                </li>

                                <li className="page-item disabled">
                                  <span className="page-link" style={{ border: 0 }}>
                                    {userCurrentPage}
                                  </span>
                                </li>

                                <li className={`page-item ${userCurrentPage === totalUserPages ? 'disabled' : ''}`}>
                                  <button
                                    className="page-link"
                                    style={{ border: 0 }}
                                    onClick={() => handleUserPageChange(userCurrentPage + 1)}
                                  >
                                    <i className="fa fa-angle-right" />
                                  </button>
                                </li>

                                <li className={`page-item ${userCurrentPage === totalUserPages ? 'disabled' : ''}`}>
                                  <button
                                    className="page-link"
                                    style={{ border: 0 }}
                                    onClick={() => handleUserPageChange(totalUserPages)}
                                  >
                                    <i className="fa fa-angle-double-right" />
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </div>

                      </div>
                    </Placeholder>
                  </div>
                </div>
              </>
            )}

            {!loading && !data && (
              <div className="text-center text-muted py-5">
                No data available
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={loading}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AreaDetailModal
